import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { authApiClient } from "../../../utils/axios";
import { showSnackbar } from "../../../api/snackbarSlice";
import { Button, Grid, Stack, Typography } from "@mui/material";
import CustomTextInput from "../../../components/CustomTextInput";
import { loginSuccess } from "../../../api/auth/authSlice";
import appConfig from "../../../config/appConfig";
import {
  applySelectedSession,
  persistAuthSession,
  resolvePostAuthRedirect,
  resolveSafePostAuthPath,
  selectOrganization,
} from "../../../utils/authToken";
import { useQueryClient } from "@tanstack/react-query";

const OTPVerificationForm = ({
  email,
  isLogin,
  orgMfa = false,
  organizationId,
  redirectTo: redirectToProp = "/sales",
}) => {
  const [loading, setLoading] = useState(false);
  const [timer, setTimer] = useState(120);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const state = location.state || {};
  const redirectTo = resolveSafePostAuthPath(
    state?.redirectTo || redirectToProp,
  );
  const resolvedOrgMfa = orgMfa || Boolean(state.orgMfa);
  const resolvedOrganizationId = organizationId || state.organizationId;
  const resolvedEmail = email || state.email;

  const {
    control,
    handleSubmit,
    formState: { errors },
    setError,
  } = useForm({
    defaultValues: {
      email: resolvedEmail,
      otp: "",
    },
  });

  const onSubmit = async (data) => {
    setLoading(true);
    const route = "/verify-mfa";
    authApiClient
      .post(route, data)
      .then((res) => {
        if (resolvedOrgMfa) {
          applySelectedSession(dispatch, queryClient);
          navigate(redirectTo, { replace: true });
          return;
        }
        if (!isLogin) {
          dispatch(
            showSnackbar({
              message: "OTP successfully verified!",
              severity: "success",
            }),
          );
          navigate("/login", { replace: true });
        } else {
          const { requiresOrgSelection } = persistAuthSession({
            requiresOrgSelection: !!res.data.requires_org_selection,
          });
          dispatch(loginSuccess({ requiresOrgSelection }));
          const nextPath = resolvePostAuthRedirect(
            requiresOrgSelection,
            redirectTo,
          );
          navigate(nextPath, {
            replace: true,
            state: requiresOrgSelection ? { redirectTo } : undefined,
          });
        }
      })
      .catch((err) => {
        if (err?.response?.data?.errors?.otp) {
          setError("otp", { message: err?.response?.data?.errors?.otp });
        } else {
          dispatch(
            showSnackbar({
              message:
                err?.response?.data?.errors?.message ?? "Error verifying OTP",
              severity: "error",
            }),
          );
        }
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    // Show snackbar message if coming from login redirect
    if (isLogin && location.state?.message) {
      dispatch(
        showSnackbar({
          message: location.state.message,
          severity: "success",
        }),
      );
    }
  }, [isLogin, location.state, dispatch]);

  useEffect(() => {
    if (timer > 0) {
      const countdown = setInterval(() => {
        setTimer(timer - 1);
      }, 1000);
      return () => clearInterval(countdown);
    }
  }, [timer]);

  const handleResendOtp = () => {
    const resendRequest = resolvedOrgMfa
      ? selectOrganization(authApiClient, resolvedOrganizationId)
      : authApiClient.post("/generate-otp-new-login", {
          email: resolvedEmail,
          isLogin,
        });

    resendRequest
      .then((res) => {
        const message = resolvedOrgMfa
          ? res?.message || "OTP sent successfully"
          : "OTP sent successfully";

        dispatch(
          showSnackbar({
            message,
            severity: "success",
          }),
        );
        setTimer(120);
      })
      .catch((err) => {
        dispatch(
          showSnackbar({
            message:
              err?.response?.data?.errors?.message ??
              "Error sending OTP. Please try again.",
            severity: "error",
          }),
        );
      });
  };

  return (
    <>
      <Stack sx={{ alignItems: "center", mb: 4 }}>
        <img src={appConfig.brandLogoPath} alt="logo" width="50%" />
      </Stack>

      <Stack spacing={2}>
        <CustomTextInput
          name="email"
          label="Email"
          control={control}
          errors={errors}
          disabled={true}
        />
        <CustomTextInput
          name="otp"
          label="OTP"
          control={control}
          errors={errors}
        />
        <Button
          type="button"
          variant="contained"
          className="submit"
          onClick={handleSubmit(onSubmit)}
        >
          {loading ? "Verifying..." : isLogin ? "Login" : "Verify Account"}
        </Button>
        <Grid
          container
          spacing={2}
          sx={{ justifyContent: "space-between", alignItems: "center" }}
        >
          <Typography variant="body2">
            <Link to="/login" style={{ textDecoration: "none" }}>
              Back to Login
            </Link>
          </Typography>
          {timer > 0 ? (
            <Typography variant="body2" color="textSecondary" align="center">
              Resend OTP in {Math.floor(timer / 60)}:
              {String(timer % 60).padStart(2, "0")}
            </Typography>
          ) : (
            <Button
              onClick={handleResendOtp}
              align="left"
              variant="text"
              color="primary"
            >
              Resend OTP
            </Button>
          )}
        </Grid>
      </Stack>
    </>
  );
};
export default OTPVerificationForm;
